/**
 * Session utilities for transforming IDP sessions.
 */

import { format } from 'date-fns';
import type { IdpSessionResponse } from '@/api/user-portal-api-generated';

/**
 * Transformed session data with camelCase properties
 */
export interface TransformedSession {
  id: string;
  createdAt: Date;
  expiresAt: Date;
  ipAddress: string | null;
  userAgent: string | null;
  isCurrentSession: boolean;
  isDeviceBound: boolean;
  /** Enrolled mobile device UUID; null for browser/web sessions. */
  deviceId: string | null;
  /** Display name: enrolled device name (mobile) or "{browser} on {os}" (web). */
  deviceName: string | null;
  device: {
    type: string | null;
    os: string | null;
    browser: string | null;
  } | null;
  geo: {
    city: string | null;
    region: string | null;
    countryCode: string | null;
    countryName: string | null;
  } | null;
  /** List of application names accessed during this session */
  applications: string[];
}

/**
 * Transform backend session response to frontend model
 */
export function transformSession(
  session: IdpSessionResponse
): TransformedSession {
  return {
    id: String(session.idp_session_id),
    createdAt: new Date(session.created_at),
    expiresAt: new Date(session.expires_at),
    ipAddress: session.ip_address ?? null,
    userAgent: session.device?.user_agent ?? null,
    isCurrentSession: session.is_current_session ?? false,
    isDeviceBound: session.is_device_bound ?? false,
    deviceId: session.device_id ?? null,
    deviceName: session.device_name ?? null,
    device: session.device
      ? {
          type: session.device.type ?? null,
          os: session.device.os ?? null,
          browser: session.device.browser ?? null,
        }
      : null,
    geo: session.geo
      ? {
          city: session.geo.city ?? null,
          region: session.geo.region ?? null,
          countryCode: session.geo.country_code ?? null,
          countryName: session.geo.country_name ?? null,
        }
      : null,
    applications: session.applications ?? [],
  };
}

/**
 * Format date for display (e.g., "Jan 3, 11:44 AM")
 */
export function formatSessionDate(date: Date): string {
  return format(date, 'MMM d, h:mm a');
}

/**
 * Format location for display (e.g., "San Francisco, US")
 */
export function formatLocation(geo: TransformedSession['geo']): string | null {
  if (!geo) {
    return null;
  }

  const parts: string[] = [];

  if (geo.city) {
    parts.push(geo.city);
  }

  if (geo.countryCode) {
    parts.push(geo.countryCode);
  }

  return parts.length > 0 ? parts.join(', ') : null;
}
